/**
 * Fake Chat IA conversations for demo mode.
 * All IDs are prefixed with "demo-" so the page can detect them
 * and serve local data instead of hitting the API.
 */

export interface DemoChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  meta: null;
  createdAt: string;
}

export interface DemoChatConversation {
  id: string;
  workspaceId: null;
  title: string;
  lastMessageAt: string;
  createdAt: string;
  lastMessagePreview: string;
  messages: DemoChatMessage[];
}

function ago(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

export function getDemoChatConversations(lang: string): DemoChatConversation[] {
  const fr = lang !== "en";

  return [
    {
      id: "demo-conv-planning",
      workspaceId: null,
      title: fr ? "Planification de la semaine" : "Week planning",
      lastMessageAt: ago(45),
      createdAt: ago(50),
      lastMessagePreview: fr
        ? "Voici ton plan pour la semaine, organisé par priorité…"
        : "Here's your plan for the week, organized by priority…",
      messages: [
        {
          id: "demo-msg-p1",
          role: "user",
          content: fr
            ? "J'ai 3 projets actifs, 18 tâches en attente et une réunion importante jeudi. Aide-moi à organiser ma semaine."
            : "I have 3 active projects, 18 pending tasks and an important meeting on Thursday. Help me organize my week.",
          meta: null,
          createdAt: ago(50),
        },
        {
          id: "demo-msg-p2",
          role: "assistant",
          content: fr
            ? `Voici ton plan pour la semaine, organisé par priorité :

**Lundi — Mise en route**
- Triage de tes 18 tâches : identifie celles bloquantes et celles qui peuvent attendre
- Traitement des mails urgents (vise < 30 min)
- Avance sur le Projet A (livrable jeudi)

**Mardi & Mercredi — Production**
- Blocs de travail profond de 90 min sur le Projet A (matin)
- Avancement Projet B en après-midi
- Laisse le Projet C en attente jusqu'à jeudi soir

**Jeudi — Réunion + revue**
- Prépare 15 min avant ta réunion : agenda, points clés, décisions attendues
- Après la réunion : capture les actions dans OneNext immédiatement
- Revue de fin de semaine rapide (30 min)

**Vendredi — Clôture**
- Finir les tâches à livrer cette semaine
- Planifier les 3 priorités de la semaine prochaine
- Inbox zéro si possible

Veux-tu que je t'aide à prioriser tes 18 tâches maintenant ?`
            : `Here's your plan for the week, organized by priority:

**Monday — Warm-up**
- Triage your 18 tasks: identify blockers vs. what can wait
- Process urgent emails (aim for < 30 min)
- Progress on Project A (deliverable Thursday)

**Tuesday & Wednesday — Deep work**
- 90-min focused blocks on Project A (morning)
- Project B progress in the afternoon
- Leave Project C on hold until Thursday evening

**Thursday — Meeting + review**
- Prep 15 min before your meeting: agenda, key points, expected decisions
- After the meeting: capture action items in OneNext immediately
- Quick end-of-week review (30 min)

**Friday — Wrap-up**
- Finish tasks due this week
- Plan your top 3 priorities for next week
- Inbox zero if possible

Would you like me to help prioritize your 18 tasks now?`,
          meta: null,
          createdAt: ago(49),
        },
        {
          id: "demo-msg-p3",
          role: "user",
          content: fr
            ? "Oui, comment tu suggères de trier les tâches rapidement ?"
            : "Yes, how do you suggest sorting tasks quickly?",
          meta: null,
          createdAt: ago(48),
        },
        {
          id: "demo-msg-p4",
          role: "assistant",
          content: fr
            ? `Utilise la **matrice Urgence × Impact** en 4 quadrants :

| | **Urgent** | **Pas urgent** |
|---|---|---|
| **Impact fort** | 🔴 Faire maintenant | 🟡 Planifier cette semaine |
| **Impact faible** | 🟠 Déléguer ou grouper | ⚪ Supprimer ou reporter |

**En pratique :** parcours chaque tâche en < 10 secondes. Pose-toi juste deux questions : *Est-ce bloquant pour quelqu'un ou pour un livrable cette semaine ?* et *Est-ce que ça a un vrai impact si c'est fait ?*

Pour tes 18 tâches, je parie que 4-5 sont vraiment urgentes et à fort impact — commence par celles-là lundi matin.`
            : `Use the **Urgency × Impact matrix** with 4 quadrants:

| | **Urgent** | **Not urgent** |
|---|---|---|
| **High impact** | 🔴 Do now | 🟡 Schedule this week |
| **Low impact** | 🟠 Delegate or batch | ⚪ Drop or postpone |

**In practice:** scan each task in < 10 seconds. Ask yourself just two questions: *Is this blocking someone or a deliverable this week?* and *Does it actually matter if it gets done?*

Out of your 18 tasks, I'd bet 4-5 are genuinely urgent and high-impact — start with those on Monday morning.`,
          meta: null,
          createdAt: ago(47),
        },
        {
          id: "demo-msg-p5",
          role: "user",
          content: fr ? "Parfait, merci !" : "Perfect, thanks!",
          meta: null,
          createdAt: ago(46),
        },
        {
          id: "demo-msg-p6",
          role: "assistant",
          content: fr
            ? "Avec plaisir ! Bonne semaine productive 🚀"
            : "You're welcome! Have a productive week 🚀",
          meta: null,
          createdAt: ago(45),
        },
      ],
    },

    {
      id: "demo-conv-email",
      workspaceId: null,
      title: fr ? "Rédiger un email de relance" : "Draft a follow-up email",
      lastMessageAt: ago(24 * 60),
      createdAt: ago(24 * 60 + 10),
      lastMessagePreview: fr
        ? "Voici un brouillon de relance, professionnel et chaleureux…"
        : "Here's a follow-up draft, professional and warm…",
      messages: [
        {
          id: "demo-msg-e1",
          role: "user",
          content: fr
            ? "J'ai envoyé une proposition commerciale à un prospect il y a 10 jours, pas de réponse. Aide-moi à rédiger une relance efficace sans être trop insistant."
            : "I sent a commercial proposal to a prospect 10 days ago with no reply. Help me write an effective follow-up that isn't pushy.",
          meta: null,
          createdAt: ago(24 * 60 + 10),
        },
        {
          id: "demo-msg-e2",
          role: "assistant",
          content: fr
            ? `Voici un brouillon de relance, professionnel et chaleureux :

---

**Objet :** Relance — Proposition [nom du projet]

Bonjour [Prénom],

Je me permets de revenir vers vous suite à la proposition que je vous ai transmise le [date].

Je comprends que votre agenda est certainement chargé. Si vous avez des questions sur les modalités ou si vous souhaitez ajuster certains points, je suis disponible pour en discuter à votre convenance.

Si le timing n'est pas idéal pour le moment, pas de souci — dites-le moi simplement et je reviendrai vers vous à une date qui vous convient mieux.

Bien cordialement,
[Votre prénom]

---

**Pourquoi ça marche :** le ton est non-invasif, il ouvre deux portes de sortie (questions ou report) et se termine sans pression. Tu peux envoyer cet email tel quel ou me donner plus de contexte (secteur, montant, relation) pour l'affiner.`
            : `Here's a follow-up draft, professional and warm:

---

**Subject:** Follow-up — [Project name] Proposal

Hi [First name],

I wanted to follow up on the proposal I sent over on [date].

I know your schedule is likely busy. If you have any questions about the details or would like to tweak anything, I'm happy to jump on a quick call at your convenience.

If the timing isn't quite right at the moment, no worries at all — just let me know and I'll circle back when it works better for you.

Best regards,
[Your name]

---

**Why it works:** the tone is non-invasive, it opens two exit doors (questions or postponement) and ends without pressure. Feel free to send this as-is, or give me more context (industry, amount, relationship) to fine-tune it.`,
          meta: null,
          createdAt: ago(24 * 60 + 9),
        },
        {
          id: "demo-msg-e3",
          role: "user",
          content: fr
            ? "C'est bien mais peux-tu le rendre un peu plus court ?"
            : "That's good but can you make it a bit shorter?",
          meta: null,
          createdAt: ago(24 * 60 + 5),
        },
        {
          id: "demo-msg-e4",
          role: "assistant",
          content: fr
            ? `Version courte :

---

**Objet :** Relance — [nom du projet]

Bonjour [Prénom],

Je reviens vers vous suite à ma proposition du [date]. Avez-vous eu l'occasion d'y jeter un œil ?

N'hésitez pas à me faire signe si vous avez des questions ou si vous souhaitez qu'on en reparle à un autre moment.

Bonne journée,
[Votre prénom]

---

10 lignes, direct et respectueux. Tu veux que j'adapte encore ?`
            : `Short version:

---

**Subject:** Follow-up — [project name]

Hi [First name],

Just following up on the proposal I sent on [date]. Have you had a chance to look it over?

Feel free to reach out if you have any questions, or if a different time would work better.

Best,
[Your name]

---

10 lines, direct and respectful. Want me to tweak it further?`,
          meta: null,
          createdAt: ago(24 * 60),
        },
      ],
    },

    {
      id: "demo-conv-recap",
      workspaceId: null,
      title: fr ? "Résumé réunion fournisseur" : "Supplier meeting summary",
      lastMessageAt: ago(3 * 24 * 60 - 30),
      createdAt: ago(3 * 24 * 60),
      lastMessagePreview: fr
        ? "Voici le résumé structuré de ta réunion avec les actions clés…"
        : "Here's the structured summary of your meeting with key actions…",
      messages: [
        {
          id: "demo-msg-r1",
          role: "user",
          content: fr
            ? `Voici mes notes brutes de réunion avec notre fournisseur logistique :

- Délais de livraison : passer de 5j à 3j possible si commande avant jeudi
- Tarifs : remise 8% dès 500 unités/mois
- Contact : Sophie Martin, nouveau responsable compte
- Prochain RDV : mi-juin pour bilan Q2
- Question en suspens : intégration API avec notre ERP

Peux-tu en faire un résumé structuré avec les actions à faire ?`
            : `Here are my raw notes from the meeting with our logistics supplier:

- Delivery times: can go from 5 days to 3 days if orders placed before Thursday
- Pricing: 8% discount at 500+ units/month
- Contact: Sophie Martin, new account manager
- Next meeting: mid-June for Q2 review
- Open question: API integration with our ERP

Can you make a structured summary with action items?`,
          meta: null,
          createdAt: ago(3 * 24 * 60),
        },
        {
          id: "demo-msg-r2",
          role: "assistant",
          content: fr
            ? `Voici le résumé structuré de ta réunion avec les actions clés :

## 📋 Résumé — Réunion fournisseur logistique

**Date :** [à compléter]
**Contact :** Sophie Martin (nouveau responsable compte)

---

### Points clés
| Sujet | Détail |
|---|---|
| Délais de livraison | 5j → 3j possible si commande passée avant **jeudi** |
| Remise tarifaire | **8%** à partir de 500 unités/mois |
| Prochain RDV | Mi-juin — bilan Q2 |

---

### ✅ Actions à faire
1. **Vérifier notre volume mensuel actuel** — sommes-nous proches de 500 unités ? *(responsable : toi)*
2. **Contacter Sophie Martin** pour confirmer les nouvelles conditions — mettre à jour le CRM avec ses coordonnées
3. **Évaluer l'intégration API ERP** — lancer une spec technique avec l'équipe IT *(priorité : avant mi-juin)*
4. **Planifier le RDV de mi-juin** dans l'agenda

---

### ❓ Point en suspens
- Intégration API avec l'ERP : à creuser côté technique avant le prochain RDV.

Veux-tu que je crée ces tâches directement dans OneNext ?`
            : `Here's the structured summary of your meeting with key actions:

## 📋 Summary — Logistics supplier meeting

**Date:** [to fill in]
**Contact:** Sophie Martin (new account manager)

---

### Key points
| Topic | Detail |
|---|---|
| Delivery times | 5 days → 3 days possible if order placed before **Thursday** |
| Pricing discount | **8%** starting at 500 units/month |
| Next meeting | Mid-June — Q2 review |

---

### ✅ Action items
1. **Check our current monthly volume** — are we close to 500 units? *(owner: you)*
2. **Contact Sophie Martin** to confirm new terms — update CRM with her details
3. **Assess ERP API integration** — launch a technical spec with IT team *(priority: before mid-June)*
4. **Schedule mid-June meeting** in the calendar

---

### ❓ Open item
- ERP API integration: needs technical investigation before the next meeting.

Would you like me to create these tasks directly in OneNext?`,
          meta: null,
          createdAt: ago(3 * 24 * 60 - 25),
        },
        {
          id: "demo-msg-r3",
          role: "user",
          content: fr
            ? "Super résumé ! Oui crée-moi les tâches."
            : "Great summary! Yes, create the tasks for me.",
          meta: null,
          createdAt: ago(3 * 24 * 60 - 20),
        },
        {
          id: "demo-msg-r4",
          role: "assistant",
          content: fr
            ? `J'ai créé 4 tâches dans OneNext :

- ✅ **Vérifier volume mensuel fournisseur** — priorité haute, pour aujourd'hui
- ✅ **Contacter Sophie Martin + maj CRM** — priorité normale, cette semaine
- ✅ **Spec technique intégration API ERP** — priorité haute, avant mi-juin
- ✅ **Planifier RDV bilan Q2 fournisseur** — priorité normale

Tu les retrouves dans "À faire" avec les bonnes échéances. N'oublie pas de noter ses coordonnées dans le CRM !`
            : `I've created 4 tasks in OneNext:

- ✅ **Check supplier monthly volume** — high priority, for today
- ✅ **Contact Sophie Martin + update CRM** — normal priority, this week
- ✅ **Technical spec for ERP API integration** — high priority, before mid-June
- ✅ **Schedule Q2 supplier review meeting** — normal priority

You'll find them in "To-do" with the right due dates. Don't forget to save her contact details in your CRM!`,
          meta: null,
          createdAt: ago(3 * 24 * 60 - 15),
        },
      ],
    },
  ];
}
